import { Box } from "@chakra-ui/react";
import React from "react";

type Point = { x: number; y: number };

/** A rectangle in pixels, relative to the top-left corner of the overlay (and the map under it). */
export type PixelRect = { minX: number; minY: number; maxX: number; maxY: number };

/** Anything smaller is treated as a click rather than a drawn rectangle. */
const MIN_DRAG_SIZE_PX = 4;

type Props = {
  isAreaSelectMode: boolean;
  onExitAreaSelectMode: () => void;
  onSelectArea: (rect: PixelRect) => void;
};

/**
 * Covers the map while a rectangle can be drawn - either in "Select area" mode or while Shift is
 * held - so the drag draws a rectangle instead of panning the map.
 */
export default function MapAreaSelectOverlay(props: Props) {
  const [isShiftHeld, setIsShiftHeld] = React.useState(false);
  const [drag, setDrag] = React.useState<{ start: Point; current: Point } | null>(null);
  const { onExitAreaSelectMode } = props;

  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        case "Shift":
          setIsShiftHeld(true);
          return;
        case "Escape":
          setDrag(null);
          onExitAreaSelectMode();
          return;
      }
    }

    function handleKeyUp(event: KeyboardEvent) {
      if (event.key === "Shift") {
        setIsShiftHeld(false);
      }
    }

    // A keyup that happens while the window is not focused never arrives.
    function handleBlur() {
      setIsShiftHeld(false);
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [onExitAreaSelectMode]);

  // Keep the overlay while dragging even if Shift is released mid-drag.
  const isActive = props.isAreaSelectMode || isShiftHeld || drag !== null;

  if (!isActive) {
    return null;
  }

  function toLocalPoint(event: React.PointerEvent<HTMLDivElement>): Point {
    const bounds = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toLocalPoint(event);
    setDrag({ start: point, current: point });
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (drag === null) {
      return;
    }

    setDrag({ start: drag.start, current: toLocalPoint(event) });
  }

  function handlePointerUp() {
    if (drag === null) {
      return;
    }

    const rect = toRect(drag.start, drag.current);
    setDrag(null);

    if (rect.maxX - rect.minX < MIN_DRAG_SIZE_PX && rect.maxY - rect.minY < MIN_DRAG_SIZE_PX) {
      return;
    }

    props.onSelectArea(rect);

    if (props.isAreaSelectMode) {
      props.onExitAreaSelectMode();
    }
  }

  const rect = drag !== null ? toRect(drag.start, drag.current) : null;

  return (
    <Box
      cursor="crosshair"
      inset={0}
      position="absolute"
      style={{ touchAction: "none" }}
      userSelect="none"
      zIndex={1}
      onPointerCancel={() => setDrag(null)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {rect !== null && (
        <Box
          bg="rgba(49, 130, 206, 0.15)"
          border="2px solid"
          borderColor="blue.500"
          height={`${rect.maxY - rect.minY}px`}
          left={`${rect.minX}px`}
          pointerEvents="none"
          position="absolute"
          top={`${rect.minY}px`}
          width={`${rect.maxX - rect.minX}px`}
        />
      )}
    </Box>
  );
}

function toRect(a: Point, b: Point): PixelRect {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}
